import type { FC } from 'react';
import React, { memo, useMemo, useCallback, useContext, Fragment, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkAvatarGroup from '@atlaskit/avatar-group';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { VIEW_PAGE_BYLINE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { ReadTime } from '@confluence/read-time/entry-points/ReadTime.ts';
import { AnalyticsByLine } from '@confluence/confluence-analytics/entry-points/AnalyticsByLine.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { PoweredByTemplates } from '@confluence/powered-by-templates';
import { BadgeStylePageOwnershipByline } from '@confluence/content-ownership/entry-points/BadgeStylePageOwnershipByline.ts';
import { PageOwnershipByline } from '@confluence/content-ownership/entry-points/PageOwnershipByline.ts';
import { AutoConversionByLine } from '@confluence/editor-conversion/entry-points/AutoConversionByLine.ts';
import {
	TitleAlignmentType,
	type TitleContentPropertiesType,
} from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState.ts';
import { ViewPageBylineReactions } from '@confluence/reactions/entry-points/ViewPageBylineReactions.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import type { TargetContentType } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { CURRENT_STATUS } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { ListenByline } from '@confluence/audio/entry-points/ListenByline.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ByLineApps } from './ByLineApps.tsx';
import { ByLineLastModified } from './ByLineLastModified.tsx';
import type { UserShape } from './byLineTypes.ts';
import { getArrangedContributors } from './byLineUtils.tsx';
import { png_1x1_n40 } from './png1x1.ts';
import { SingleLineByLine } from './SingleLineByLine.tsx';
import {
	StyledMainDiv,
	StyledAuthorDiv,
	StyledAvatars,
	StyledNameSkeleton,
	StyledTextDiv,
} from './PresentationalComponents.tsx';
import { ByLineLastUpdatedCompact } from './ByLineLastUpdatedCompact.tsx';
import { BylineClassification } from './BylineClassification.tsx';

const NO_OF_AVATARS = 4;

const BLOG_POST_SOURCE_TEMPLATE_ENTITY_ID = '00000000-0000-0000-0000-000000000001';

type version = {
	friendlyWhen: string;
	number: number;
	by: UserShape;
	contentTypeModified: boolean;
};

type ByLineProps = {
	contentId: string;
	author: UserShape;
	owner: UserShape | null;
	lastOwner: UserShape | null;
	createdDate: Date | null;
	version: version;
	contributors: UserShape[];
	contributorsCount: number;
	isOwnerContributor: boolean;
	fetchAllContributors: Function;
	versionComment?: string;
	hasByLineContributors?: boolean;
	hasByLineExtensions?: boolean;
	templateId: string | null;
	isFabricPage: boolean;
	titleContentProperties: TitleContentPropertiesType;
	shouldShowBylineReactions?: boolean;
	contentType?: string | null;
	spaceId?: string | null;
	contentSubType?: string | null;
	generatedBy?: string | null;
	isCurrentUserContributor?: boolean;
};

type userAvatarShape = {
	isAnonymous: boolean;
	userId: string;
	src: string;
	name: string;
};

const newRendererBylineLastUpdatedStyles = xcss({
	marginTop: 'space.negative.025',
	marginLeft: 'space.negative.050',
	marginRight: 'space.negative.075',
});

const NewRendererByline = ({
	showByLineContributors,
	isTitleCenterAligned,
	version,
	contentId,
	showByLineExtensions,
	isExternalShareRequest,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	owner,
	author,
	isFabricPage,
	isOwnerContributor,
	contributorsCount,
	generatedBy,
	isCurrentUserContributor,
}) => {
	const [contentTypeFromContext] = useContentType();
	const canUpdateContent = useCanPerformContentOperation({
		contentId,
		contentType: (contentType ?? contentTypeFromContext) as TargetContentType,
		operation: OperationTypes.UPDATE,
		status: CURRENT_STATUS,
	});
	const hasEditPermission = canUpdateContent ?? false;
	const { isAIEarlyAccessEnabled, isAdminHubAIEnabled } = useSessionData();

	const bylineContainerRef = useRef<HTMLDivElement>(null);

	const commonContent = (
		<>
			{showByLineContributors && (
				<>
					<BadgeStylePageOwnershipByline
						owner={owner}
						author={author}
						analyticsSource="renderer"
						contributorCountWithoutOwner={contributorsCount - (isOwnerContributor ? 1 : 0)}
						generatedBy={generatedBy}
					/>
					<BylineClassification contentId={contentId} />
					{expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
						<EditorContentState
							contentId={contentId}
							contentType={(contentType ?? contentTypeFromContext) as 'page' | 'blogpost' | null}
							isViewMode
							canEdit={hasEditPermission}
							userOffline={false}
						/>
					) : (
						<RendererContentState contentId={contentId} isDraft={false} hasSeparator={false} />
					)}
					{((!isFabricPage && fg('confluence_frontend_content_wrapper')) ||
						!fg('confluence_frontend_content_wrapper')) && (
						<Box xcss={newRendererBylineLastUpdatedStyles}>
							<ByLineLastUpdatedCompact version={version} />
						</Box>
					)}
				</>
			)}
			{showByLineExtensions && (
				<>
					<ReadTime contentId={contentId} />
					{isAdminHubAIEnabled &&
						(!isExperimentEnabled('confluence_audio_labs_lozenge') || isAIEarlyAccessEnabled) &&
						isExperimentEnabled('confluence_audio_v3') &&
						isExperimentEnabled('confluence_ai_consumption_byline') && (
							<ListenByline contentId={contentId} contentType={contentType} />
						)}
					<AnalyticsByLine contentId={contentId} contentAuthorId={author.userId} />
					{shouldShowBylineReactions && (
						<ViewPageBylineReactions
							contentId={contentId}
							contentType={contentType}
							spaceId={spaceId}
							contentSubType={contentSubType}
							noFlexWrap
							noRelativeContainer
							bylineContainerRef={bylineContainerRef}
							isCurrentUserContributor={isCurrentUserContributor}
						/>
					)}
					{!isExternalShareRequest && <AutoConversionByLine />}
				</>
			)}
		</>
	);

	return (
		<StyledMainDiv
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className="page-metadata-modification-info"
			isTitleCenterAligned={isTitleCenterAligned}
			ref={bylineContainerRef}
		>
			<StyledTextDiv hasBadgeStylePageOwnerBylineItem>
				<SingleLineByLine
					contentId={contentId}
					isTitleCenterAlignedOverride={isTitleCenterAligned}
					showByLineExtensions={showByLineExtensions}
				>
					{commonContent}
				</SingleLineByLine>
			</StyledTextDiv>
		</StyledMainDiv>
	);
};

const DefaultRendererByline = ({
	showByLineContributors,
	isTitleCenterAligned,
	renderAvatarGroup,
	CreatorOrOwnerByline,
	version,
	author,
	contentId,
	versionComment,
	showByLineExtensions,
	isExternalShareRequest,
}) => (
	<StyledMainDiv
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		className="page-metadata-modification-info"
		data-testid="page-main-div"
		isTitleCenterAligned={isTitleCenterAligned}
	>
		{showByLineContributors && !isTitleCenterAligned && renderAvatarGroup}
		<StyledTextDiv>
			{showByLineContributors && (
				<StyledAuthorDiv isTitleCenterAligned={isTitleCenterAligned}>
					{isTitleCenterAligned && renderAvatarGroup}
					{CreatorOrOwnerByline}
				</StyledAuthorDiv>
			)}
			<div>
				{showByLineContributors && (
					<ByLineLastModified
						version={version}
						author={author}
						contentId={contentId}
						versionComment={versionComment}
					/>
				)}
				{showByLineExtensions && (
					<Fragment>
						<ReadTime contentId={contentId} />
						<AnalyticsByLine contentId={contentId} contentAuthorId={author.userId} />
						{!isExternalShareRequest && (
							<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
								<ByLineApps contentId={contentId} />
							</ErrorBoundary>
						)}
						{!isExternalShareRequest && <AutoConversionByLine />}
					</Fragment>
				)}
			</div>
		</StyledTextDiv>
		<ExperienceSuccess name={VIEW_PAGE_BYLINE_EXPERIENCE} />
	</StyledMainDiv>
);

export const ByLineWithContributorsComponent: FC<ByLineProps> = memo(
	({
		contentId,
		author,
		owner,
		createdDate,
		version,
		versionComment,
		contributors,
		contributorsCount,
		isOwnerContributor,
		fetchAllContributors,
		hasByLineContributors,
		hasByLineExtensions,
		templateId,
		isFabricPage,
		lastOwner,
		titleContentProperties,
		shouldShowBylineReactions,
		contentType,
		contentSubType,
		spaceId,
		generatedBy,
		isCurrentUserContributor,
	}) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { isExternalShareRequest } = useContext(ExternalShareContext);

		const isTitleCenterAligned =
			titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

		const getContributorsData = useMemo(() => {
			const lastModifier: UserShape = version.by;

			const arrangedContributors: UserShape[] = getArrangedContributors({
				owner,
				author,
				lastModifier,
				contributors,
			});

			const userToAvatarGroupDataItem: (user) => userAvatarShape = (user) => ({
				// Props for <ProfileAvatar>
				isAnonymous: user.isAnonymous,
				userId: user.userId,
				src: user.avatarUrl,
				// Extra props for <AkAvatarGroup>
				name: user.fullName,
			});

			return [
				...arrangedContributors
					// Actual data for the users we know about
					.map(userToAvatarGroupDataItem),
				// Skeletons for the ones we don't
				...Array(Math.max(contributorsCount - arrangedContributors.length, 0))
					.fill(null)
					.map(() => ({
						src: png_1x1_n40,
						name: <StyledNameSkeleton />,
					})),
			];
		}, [contributorsCount, version, author, contributors, owner]);

		const allContributorsMayBeNeeded = useCallback(() => {
			if (contributorsCount === contributors.length) return;
			if (!fetchAllContributors) return;

			// Analytics event is for measuring how many prefetches may be wasteful.
			// Only fire 'dataPrefetched' if we actually start a prefetch
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'dataPrefetched',
					actionSubject: 'moreButton',
					source: 'bylineContributors',
				},
			}).fire();

			fetchAllContributors();
		}, [createAnalyticsEvent, fetchAllContributors, contributors, contributorsCount]);

		const allContributorsAreNeeded = useCallback(() => {
			// Analytics event is for measuring how many prefetches may be wasteful.
			// Always fire 'dataNeeded' when a click/tap on the +x button happened,
			// regardless of whether we still need to fetch more data.
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'dataNeeded',
					actionSubject: 'moreButton',
					source: 'bylineContributors',
				},
			}).fire();

			if (contributorsCount === contributors.length) return;
			if (!fetchAllContributors) return;

			fetchAllContributors();
		}, [createAnalyticsEvent, fetchAllContributors, contributors, contributorsCount]);

		const showMoreButtonProps = useMemo(
			() => ({
				onMouseEnter: allContributorsMayBeNeeded,
				onClick: allContributorsAreNeeded,
			}),
			[allContributorsAreNeeded, allContributorsMayBeNeeded],
		);

		const isBlogPost = templateId === BLOG_POST_SOURCE_TEMPLATE_ENTITY_ID;

		const showByLinePoweredByTemplates = Boolean(
			!isExternalShareRequest && isFabricPage && !isBlogPost && templateId,
		);
		const showByLineContributors = hasByLineContributors ?? true;
		const showByLineExtensions = hasByLineExtensions ?? true;

		const CreatorOrOwnerByline = useMemo(() => {
			if (owner !== null) {
				return (
					<PageOwnershipByline
						owner={owner}
						creator={author}
						createdDate={createdDate}
						templateId={showByLinePoweredByTemplates ? templateId : null}
						contentId={contentId}
						lastOwner={lastOwner}
						generatedBy={generatedBy}
					/>
				);
			} else {
				const linkOnly = (
					<ProfileLink
						userId={author.userId}
						fullName={author.fullName}
						isAnonymous={author.isAnonymous}
						referralSource="profilecard"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ textDecorationColor: token('color.link') }}
					/>
				);
				const profileLink = (
					<ProfileCardWrapper userId={author.userId} fullName={author.fullName}>
						{linkOnly}
					</ProfileCardWrapper>
				);

				return generatedBy === 'ONBOARDING' ? (
					<FormattedMessage
						id="content-header.by-line.created.by.expanded.created-by-confluence"
						defaultMessage="{showByLinePoweredByTemplates, select, true {Created by {author}, created by Confluence, {withTemplate}} false {Created by {author}, created by Confluence} other {Created by {author}, created by Confluence}}"
						description="Text to show author details as well as if a page was created with a template"
						values={{
							author: profileLink,
							withTemplate:
								showByLinePoweredByTemplates && !!templateId ? (
									<PoweredByTemplates templateId={templateId} />
								) : (
									''
								),
							showByLinePoweredByTemplates,
						}}
					/>
				) : (
					<FormattedMessage
						id="content-header.by-line.created.by.expanded"
						defaultMessage="{showByLinePoweredByTemplates, select, true {Created by {author}, {withTemplate}} false {Created by {author}} other {Created by {author}}}"
						description="Text to show author details as well as if a page was created with a template"
						values={{
							author: profileLink,
							withTemplate:
								showByLinePoweredByTemplates && !!templateId ? (
									<PoweredByTemplates templateId={templateId} />
								) : (
									''
								),
							showByLinePoweredByTemplates,
						}}
					/>
				);
			}
		}, [
			author,
			owner,
			createdDate,
			templateId,
			showByLinePoweredByTemplates,
			contentId,
			lastOwner,
			generatedBy,
		]);

		const renderAvatarGroup = useMemo(() => {
			return (
				<StyledAvatars isTitleCenterAligned={isTitleCenterAligned}>
					<AkAvatarGroup
						appearance="stack"
						// @ts-ignore
						data={getContributorsData}
						avatar={ProfileAvatar}
						maxCount={NO_OF_AVATARS}
						size={isTitleCenterAligned ? 'small' : 'medium'}
						showMoreButtonProps={showMoreButtonProps}
						testId="renderer-byline-contributors"
					/>
				</StyledAvatars>
			);
		}, [getContributorsData, isTitleCenterAligned, showMoreButtonProps]);

		return fg('cc_page_experiences_new_editor_byline') ? (
			<NewRendererByline
				contentId={contentId}
				version={version}
				showByLineContributors={showByLineContributors}
				showByLineExtensions={showByLineExtensions}
				isExternalShareRequest={isExternalShareRequest}
				isTitleCenterAligned={isTitleCenterAligned}
				shouldShowBylineReactions={shouldShowBylineReactions}
				contentType={contentType}
				contentSubType={contentSubType}
				spaceId={spaceId}
				owner={owner}
				author={author}
				isFabricPage={isFabricPage}
				isOwnerContributor={isOwnerContributor}
				contributorsCount={contributorsCount}
				generatedBy={generatedBy}
				isCurrentUserContributor={isCurrentUserContributor}
			/>
		) : (
			<DefaultRendererByline
				contentId={contentId}
				author={author}
				version={version}
				versionComment={versionComment}
				showByLineContributors={showByLineContributors}
				showByLineExtensions={showByLineExtensions}
				isExternalShareRequest={isExternalShareRequest}
				isTitleCenterAligned={isTitleCenterAligned}
				renderAvatarGroup={renderAvatarGroup}
				CreatorOrOwnerByline={CreatorOrOwnerByline}
			/>
		);
	},
);
